import { useEffect, useMemo, type ReactNode } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";
import { useDeliverApi } from "@bfc-software/bfc-web-ui/lib/deliver-api-client";

import {
  PAGE_SIZE,
  filterQuery,
  type ResolvedFilters,
  type Worklist,
} from "../-data/filters";
import { formatCount, formatPercent } from "../-data/format";
import { jsonBody } from "../-data/json-body";

import { PagedFrame } from "./paged-frame";
import styles from "./worklist-table.module.css";

type Props = {
  worklist: Worklist;
  filters: ResolvedFilters;
  page: number;
  onPage: (page: number) => void;
  /** Open a row's route in the drawer. */
  onOpenRoute: (route: { warehouse: number; routeId: string }) => void;
  refetchInterval: number | false;
  /**
   * Told when the list's data was last fetched (epoch ms, 0 before the first
   * answer), so the page can say so beside the list it is showing. The queries
   * live here, so this is how the page hears about them.
   */
  onUpdated: (at: number) => void;
  lang: string;
  t: TFunction;
};

/** Report a query's last-fetched time upward whenever it changes. */
function useReportUpdated(at: number, onUpdated: (at: number) => void) {
  useEffect(() => {
    onUpdated(at);
  }, [at, onUpdated]);
}

/** What every worklist's query and table are handed. */
type ListProps = Omit<Props, "worklist">;

function Header({ children }: { children: ReactNode }) {
  return <TableHeaderCell>{children}</TableHeaderCell>;
}

/** The route a row belongs to, as a way into that route's drawer. */
function RouteCell({
  row,
  onOpenRoute,
}: {
  row: { warehouse: number; routeId: string; route: string };
  onOpenRoute: ListProps["onOpenRoute"];
}) {
  return (
    <div className={styles.stacked}>
      <Button
        kind="link"
        variant="accent"
        text={row.routeId}
        onPress={() => onOpenRoute(row)}
      />
      <span className={styles.secondary}>{row.route}</span>
    </div>
  );
}

function DriverCell({
  driver,
  driverName,
  none,
}: {
  driver: string;
  driverName?: string;
  none: string;
}) {
  return (
    <div className={styles.stacked}>
      <span className={styles.primary}>{driverName || none}</span>
      <span className={styles.secondary}>{driver}</span>
    </div>
  );
}

/** The query params shared by all four endpoints. */
function useListQuery({ filters, page }: ListProps) {
  return useMemo(
    () => ({ ...filterQuery(filters), page, size: PAGE_SIZE }),
    [filters, page],
  );
}

function ScanExceptions(props: ListProps) {
  const { t, page, onPage, onOpenRoute } = props;
  const client = useDeliverApi();
  const query = useListQuery(props);
  const result = client.useQuery(
    "get",
    "/app/deliver/api/exceptions",
    { params: { query } },
    { refetchInterval: props.refetchInterval },
  );
  const data = jsonBody(result.data);
  useReportUpdated(result.dataUpdatedAt, props.onUpdated);
  const rows = data?.rows ?? [];
  const none = t("none");

  return (
    <PagedFrame
      testRole="worklist-scan-exceptions-list"
      isLoading={result.isLoading}
      isError={result.isError}
      isEmpty={rows.length === 0}
      emptyText={t("worklists.empty.scanExceptions")}
      page={page}
      hasMore={data?.hasMore ?? false}
      isFetching={result.isFetching}
      onPage={onPage}
      t={t}
    >
      <Table
        aria-label={t("worklists.scanExceptions.title")}
        isResizable={false}
      >
        <TableHead>
          <TableHeaderCell isRowHeader={true}>
            {t("worklists.columns.route")}
          </TableHeaderCell>
          <Header>{t("worklists.columns.driver")}</Header>
          <Header>{t("worklists.columns.timestamp")}</Header>
          <Header>{t("worklists.columns.stop")}</Header>
          <Header>{t("worklists.columns.item")}</Header>
          <Header>{t("worklists.columns.description")}</Header>
          <Header>{t("worklists.columns.scanId")}</Header>
          <Header>{t("worklists.columns.truck")}</Header>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.warehouse}:${row.routeId}:${row.scanId}:${row.timestamp}`}
              data-test-role="worklist-row"
            >
              <TableCell>
                <RouteCell row={row} onOpenRoute={onOpenRoute} />
              </TableCell>
              <TableCell>
                <DriverCell
                  driver={row.driver}
                  driverName={row.driverName}
                  none={none}
                />
              </TableCell>
              <TableCell>
                <span className={styles.nowrap}>{row.timestamp}</span>
              </TableCell>
              <TableCell>
                <div className={styles.stacked}>
                  <span>{row.actualStop}</span>
                  {row.intendedStop !== row.actualStop ? (
                    <span className={styles.secondary}>
                      {t("worklists.columns.intended", {
                        stop: row.intendedStop,
                      })}
                    </span>
                  ) : null}
                </div>
              </TableCell>
              <TableCell>{row.item}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.scanId}</TableCell>
              <TableCell>{row.truck || none}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PagedFrame>
  );
}

function NonScanned(props: ListProps) {
  const { t, lang, page, onPage, onOpenRoute } = props;
  const client = useDeliverApi();
  const query = useListQuery(props);
  const result = client.useQuery(
    "get",
    "/app/deliver/api/non-scanned",
    { params: { query } },
    { refetchInterval: props.refetchInterval },
  );
  const data = jsonBody(result.data);
  useReportUpdated(result.dataUpdatedAt, props.onUpdated);
  const rows = data?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <PagedFrame
      testRole="worklist-non-scanned-list"
      isLoading={result.isLoading}
      isError={result.isError}
      isEmpty={rows.length === 0}
      emptyText={t("worklists.empty.nonScanned")}
      page={page}
      hasMore={data?.hasMore ?? false}
      isFetching={result.isFetching}
      onPage={onPage}
      t={t}
    >
      <Table aria-label={t("worklists.nonScanned.title")} isResizable={false}>
        <TableHead>
          <TableHeaderCell isRowHeader={true}>
            {t("worklists.columns.route")}
          </TableHeaderCell>
          <Header>{t("worklists.columns.stop")}</Header>
          <Header>{t("worklists.columns.item")}</Header>
          <Header>{t("worklists.columns.description")}</Header>
          <Header>{t("worklists.columns.uom")}</Header>
          <Header>{t("worklists.columns.operation")}</Header>
          <Header>{t("worklists.columns.delivered")}</Header>
          <Header>{t("worklists.columns.scanPct")}</Header>
          <Header>{t("worklists.columns.returns")}</Header>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.warehouse}:${row.routeId}:${row.stop}:${row.item}:${row.uom}:${row.operation}`}
              data-test-role="worklist-row"
            >
              <TableCell>
                <RouteCell row={row} onOpenRoute={onOpenRoute} />
              </TableCell>
              <TableCell>{row.stop}</TableCell>
              <TableCell>{row.item}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.uom}</TableCell>
              <TableCell>{row.operation || none}</TableCell>
              <TableCell>{n(row.piecesDelivered)}</TableCell>
              <TableCell>{formatPercent(row.scanPct, lang) ?? none}</TableCell>
              <TableCell>{n(row.currentReturns)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PagedFrame>
  );
}

function InvoiceExceptions(props: ListProps) {
  const { t, lang, page, onPage, onOpenRoute } = props;
  const client = useDeliverApi();
  const query = useListQuery(props);
  const result = client.useQuery(
    "get",
    "/app/deliver/api/invoice-exceptions",
    { params: { query } },
    { refetchInterval: props.refetchInterval },
  );
  const data = jsonBody(result.data);
  useReportUpdated(result.dataUpdatedAt, props.onUpdated);
  const rows = data?.rows ?? [];
  const none = t("none");
  const money = (value: number | null | undefined) =>
    value === null || value === undefined
      ? none
      : new Intl.NumberFormat(lang, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);

  return (
    <PagedFrame
      testRole="worklist-invoice-exceptions-list"
      isLoading={result.isLoading}
      isError={result.isError}
      isEmpty={rows.length === 0}
      emptyText={t("worklists.empty.invoiceExceptions")}
      page={page}
      hasMore={data?.hasMore ?? false}
      isFetching={result.isFetching}
      onPage={onPage}
      t={t}
    >
      <Table
        aria-label={t("worklists.invoiceExceptions.title")}
        isResizable={false}
      >
        <TableHead>
          <TableHeaderCell isRowHeader={true}>
            {t("worklists.columns.route")}
          </TableHeaderCell>
          <Header>{t("worklists.columns.driver")}</Header>
          <Header>{t("worklists.columns.stop")}</Header>
          <Header>{t("worklists.columns.invoice")}</Header>
          <Header>{t("worklists.columns.tax")}</Header>
          <Header>{t("worklists.columns.hostTax")}</Header>
          <Header>{t("worklists.columns.taxDifference")}</Header>
          <Header>{t("worklists.columns.total")}</Header>
          <Header>{t("worklists.columns.hostTotal")}</Header>
          <Header>{t("worklists.columns.totalDifference")}</Header>
          <Header>{t("worklists.columns.added")}</Header>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.warehouse}:${row.routeId}:${row.stop}:${row.invoice}`}
              data-test-role="worklist-row"
            >
              <TableCell>
                <RouteCell row={row} onOpenRoute={onOpenRoute} />
              </TableCell>
              <TableCell>
                <DriverCell
                  driver={row.driver}
                  driverName={row.driverName}
                  none={none}
                />
              </TableCell>
              <TableCell>{row.stop}</TableCell>
              <TableCell>{row.invoice}</TableCell>
              <TableCell>{money(row.invoiceTax)}</TableCell>
              <TableCell>{money(row.hostTax)}</TableCell>
              <TableCell>{money(row.taxDifference)}</TableCell>
              <TableCell>{money(row.invoiceTotal)}</TableCell>
              <TableCell>{money(row.hostTotal)}</TableCell>
              <TableCell>{money(row.totalDifference)}</TableCell>
              <TableCell>
                <span className={styles.nowrap}>{row.added ?? none}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PagedFrame>
  );
}

function Returns(props: ListProps) {
  const { t, lang, page, onPage, onOpenRoute } = props;
  const client = useDeliverApi();
  const query = useListQuery(props);
  const result = client.useQuery(
    "get",
    "/app/deliver/api/returns",
    { params: { query } },
    { refetchInterval: props.refetchInterval },
  );
  const data = jsonBody(result.data);
  useReportUpdated(result.dataUpdatedAt, props.onUpdated);
  const rows = data?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <PagedFrame
      testRole="worklist-returns-list"
      isLoading={result.isLoading}
      isError={result.isError}
      isEmpty={rows.length === 0}
      emptyText={t("worklists.empty.returns")}
      page={page}
      hasMore={data?.hasMore ?? false}
      isFetching={result.isFetching}
      onPage={onPage}
      t={t}
    >
      <Table aria-label={t("worklists.returns.title")} isResizable={false}>
        <TableHead>
          <TableHeaderCell isRowHeader={true}>
            {t("worklists.columns.route")}
          </TableHeaderCell>
          <Header>{t("worklists.columns.stop")}</Header>
          <Header>{t("worklists.columns.order")}</Header>
          <Header>{t("worklists.columns.customer")}</Header>
          <Header>{t("worklists.columns.item")}</Header>
          <Header>{t("worklists.columns.description")}</Header>
          <Header>{t("worklists.columns.originalQty")}</Header>
          <Header>{t("worklists.columns.processed")}</Header>
          <Header>{t("worklists.columns.remaining")}</Header>
          <Header>{t("worklists.columns.returnCode")}</Header>
          <Header>{t("worklists.columns.status")}</Header>
          <Header>{t("worklists.columns.memo")}</Header>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.warehouse}:${row.routeId}:${row.stop}:${row.item}:${row.uom}:${row.returnCode}:${row.uniquePiece}`}
              data-test-role="worklist-row"
            >
              <TableCell>
                <RouteCell row={row} onOpenRoute={onOpenRoute} />
              </TableCell>
              <TableCell>{row.stop}</TableCell>
              <TableCell>{row.order}</TableCell>
              <TableCell>{row.customerName || none}</TableCell>
              <TableCell>{row.item}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{n(row.originalQty)}</TableCell>
              <TableCell>{n(row.processedQty)}</TableCell>
              <TableCell>{n(row.remainingQty)}</TableCell>
              <TableCell>{row.returnCodeDesc || row.returnCode}</TableCell>
              <TableCell>
                <Tag
                  label={row.statusDesc || row.status}
                  display={{ tone: "neutral" }}
                />
              </TableCell>
              <TableCell>{row.memo || none}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PagedFrame>
  );
}

/**
 * The list behind one of the summary's counts. Each worklist is its own
 * endpoint with its own columns, so this is a switch rather than one
 * configurable table; what they share is the filters, the paging and the way
 * a row leads into its route's drawer.
 */
export function WorklistTable({ worklist, ...rest }: Props) {
  switch (worklist) {
    case "scan-exceptions":
      return <ScanExceptions {...rest} />;
    case "non-scanned":
      return <NonScanned {...rest} />;
    case "invoice-exceptions":
      return <InvoiceExceptions {...rest} />;
    case "returns":
      return <Returns {...rest} />;
  }
}
